import type { DeepPartial, MessageCatalog } from './types';

const en: DeepPartial<MessageCatalog> = {
  nav: {
    home: 'FalaRus',
    games: 'Games',
    partner: 'Chat',
    teachers: 'Teachers',
    stats: 'Statistics',
    profile: 'Profile',
  },
  profile: {
    tapPhoto: 'Tap to change photo',
    premiumDaysLeft: 'Premium · {{days}} days left',
    groups: {
      personal: 'Personal',
      settings: 'Settings',
      help: 'Help',
    },
    rows: {
      profile: 'Profile',
      certificates: 'Certificates',
      invite: 'Invite friends',
      textSize: 'Text size',
      notifications: 'Notifications',
      darkMode: 'Dark mode',
      language: 'Language',
      sound: 'Sound & haptics',
      help: 'Help',
      pricing: 'Plans',
      paymentHistory: 'Payment history',
      logout: 'Log out',
    },
    firstName: 'First name',
    lastName: 'Last name',
    save: 'Save',
    cancel: 'Cancel',
    nameUpdated: 'Name updated',
    avatarUpdated: 'Profile photo updated',
    avatarUploadFailed: 'Could not upload photo',
    nameRequired: 'First and last name are required',
    edit: 'Edit',
    gender: 'Gender',
    genderMale: 'Male',
    genderFemale: 'Female',
    russianLevel: 'Russian level',
    contactSecurity: 'Contact & security',
    currentPassword: 'Current password',
    newPassword: 'New password',
    profileUpdated: 'Profile updated',
  },
  home: {
    premium: 'Premium',
    patentTitle: 'Patent',
    patentSubtitle: 'Patent exam prep',
    vnzhTitle: 'Residence permit',
    vnzhSubtitle: 'Residence permit exam prep',
    questGrammar: 'Grammar',
    questGrammarSub: 'Learn the rules',
    questVocab: 'Vocabulary',
    questVocabSub: 'New words',
    questReading: 'Reading',
    questReadingSub: 'Understand texts',
    questSpeaking: 'Speaking',
    questSpeakingSub: 'Conversation practice',
    questSuhbat: 'Q&A',
    questSuhbatSub: 'Talk with the tutor',
    questStart: 'Start',
    questRepeat: 'Repeat',
    dayLabel: 'Day {{day}}',
    stepLabel: 'Step {{current}} / {{total}}',
    minutesLabel: '~{{minutes}} min',
    prevDay: 'Previous day',
    nextDay: 'Next day',
  },
  stats: {
    title: 'Statistics',
    calendarTitle: 'Activity calendar',
    calendarPrevMonth: 'Previous month',
    calendarNextMonth: 'Next month',
    kunlikGrammar: 'Grammar',
    kunlikVocab: 'Vocabulary',
    kunlikReading: 'Reading',
    kunlikSpeaking: 'Speaking',
    kunlikSuhbat: 'Q&A',
    kunlikNone: 'Not started',
    kunlikContinue: 'Continue',
    kunlikStart: 'Start',
  },
  partner: {
    title: 'Chat',
    guestTitle: 'Chat & partners',
    guestSubtitle: 'Sign in for chats, groups, and finding a partner',
    guestLogin: 'Log in',
    profiles: 'Profiles',
    profilesSub: 'Choose a partner',
    incoming: 'Incoming requests',
    outgoing: 'Outgoing requests',
    adminChat: 'Support',
    groupChat: 'Q&A group',
    mentionAlert: 'You were mentioned',
    mentionEmpty: 'Nobody found',
    mentionTitle: 'Who do you mean?',
    partnerChat: 'Partner chat',
    noRequests: 'No requests yet',
    noProfiles: 'No profiles yet',
    sendRequest: 'Send request',
    requestSent: 'Request sent',
    acceptRequest: 'Accept',
    rejectRequest: 'Decline',
    cancelRequest: 'Cancel request',
    pending: 'Awaiting response',
    male: 'Male',
    female: 'Female',
    yearsOld: '{{age}} years old',
    chatSoon: 'Chat coming soon',
    firstMessage: 'Send the first message!',
    messagePlaceholder: 'Write a message...',
    endPartnership: 'End chat',
    support: 'Support',
  },
  teachers: {
    videoTitle: 'Video introduction',
    title: 'Teachers',
    subtitle: 'Learn from experienced teachers',
    details: 'Details',
    emptyTitle: 'No teachers yet',
    emptyDesc: 'Active teachers will appear here when listed.',
    notFound: 'Teacher not found',
    about: 'About me',
    reviews: 'Student reviews',
    experience: 'Experience:',
    price: 'Monthly course:',
    formatOnline: 'Online',
    formatOffline: 'Offline',
    priceNegotiable: 'Price negotiable',
    notSpecified: 'Not specified',
    student: 'Student',
    trialBook: 'Book a trial lesson',
    trialPending: 'Payment pending',
  },
  courses: {
    title: 'Courses',
    patentTitle: 'Patent exam',
    patentSubtitle: 'Patent exam preparation',
    vnzhTitle: 'Residence permit exam',
    vnzhSubtitle: 'Residence permit exam preparation',
  },
  games: {
    title: 'Games',
    howToPlay: 'How to play',
    howToPlayText:
      'Drag across adjacent letters without lifting your finger or mouse. When you form a valid Russian word, the answer below fills in. Each cell can be used once per swipe.',
    reset: 'Reset',
    hint: 'Hint',
    stageProgress: 'Stage {{stage}} / {{total}}',
    levelLabel: 'Level {{level}}',
    nextStage: 'Next stage',
    availableStagesEnded: 'No more stages available for now',
    stageLocked: 'This stage is locked',
    stageEmpty: 'No words on this stage yet',
    gridGenerationFailed: 'Could not build the grid',
    loginRequired: 'Log in to play',
    loginToPlay: 'Log in to play',
    saveProgressFailed: 'Could not save progress',
  },
  invite: {
    title: 'Invite friends',
    subtitle: 'Invite friends and earn bonuses',
    statInvites: 'Invites',
    statRegistered: 'Registered',
    statPaid: 'Paid',
    statBalance: 'Bonus balance',
    linkMissing: 'Link unavailable',
    listTitle: 'Invite list',
    listEmpty: 'No invites yet',
    statusPaid: 'Paid',
    statusRegistered: 'Registered',
    statusInvited: 'Invite sent',
    bonusNote: 'Bonus balance updates automatically',
    shareText: 'Join FalaRus course',
  },
  help: {
    title: 'Help',
    admin: 'Admin',
    online: 'Online',
    offline: 'Offline',
    noChats: 'No chats yet',
    noMessages: 'No messages yet',
    placeholder: 'Describe your issue or question...',
    image: 'Image',
    uploadError: 'Failed to upload image',
    messagesLoading: 'Loading messages...',
  },
  notFound: {
    title: 'Page not found',
    body: 'The address may be wrong or the page was moved.',
    home: 'Home',
    login: 'Log in',
    footerPatent: 'Patent course',
    footerVnzh: 'Residence permit course',
    footerPricing: 'Plans',
    footerPrefix: 'Patent and residence permit sections:',
  },
  payment: {
    historyTitle: 'Payment history',
    statusPending: 'Pending',
    statusApproved: 'Approved',
    statusRejected: 'Rejected',
    empty: 'No payments yet',
    popular: 'Most popular',
    buyThreeMonth: '3 MONTHS',
    buyYear: '1 YEAR',
    currency: 'UZS',
    acceptedBody: 'Your payment was received.',
    acceptedDefault: 'All lessons will unlock after confirmation.',
    acceptedTeacherTrial: 'Please wait for trial lesson confirmation.',
    acceptedTitle: 'Payment received',
    back: 'Back',
    backCabinet: 'Back to cabinet',
    backProfile: 'Back to profile',
    backTeacher: 'Back to teachers',
    buyThreeMonthAction: 'Buy 3 months',
    buyYearAction: 'Buy 1 year',
    cardCopy: 'Copy card number',
    cardHolder: 'Cardholder',
    cardNumber: 'Card number',
    chooseAnotherFile: 'Choose another file',
    contactAdmin: 'Contact admin',
    course: 'Course',
    fileSizeMax: 'File size must not exceed 10 MB',
    fileTypes: 'JPG, PNG, WEBP or PDF only',
    nameCopy: 'Copy name',
    needLogin: 'Log in to pay',
    payExact: 'Transfer the exact amount',
    pendingDefault: 'Your payment is being reviewed. Access opens after approval.',
    pendingTeacherListing: 'Listing payment is being reviewed.',
    pendingTeacherTrial: 'Trial lesson payment is being reviewed.',
    pendingTitle: 'Payment pending',
    phoneCopy: 'Copy phone',
    phoneNumber: 'Phone',
    rahmatStartError: 'Failed to start Rahmat payment',
    selectOrDrop: 'Select or drop a file here',
    service: 'Service',
    submit: 'Submit payment',
    submitting: 'Submitting...',
    tariff: 'Plan',
    teacherTrialRubHint: 'Trial lesson price in rubles',
    teacherTrialUzsHint: 'Trial lesson: {{amount}} UZS (Rahmat)',
    uploadFormats: 'JPG, PNG, WEBP or PDF, max 10 MB',
    uploadReceipt: 'Upload receipt or screenshot',
    uploaded: 'uploaded',
    amountTitle: 'Payment amount',
    currencyTitle: 'Choose payment currency',
    currencySubtitle: 'Which currency do you want to pay in?',
  },
  pricing: {
    pendingNotice: 'Payment is being reviewed. Premium activates after approval.',
    alreadyActive: 'Premium already active',
    loadError: 'Failed to load plans',
    whyTitle: 'Why this course?',
    ctaEnroll: 'Enroll now',
    vocabTitle: 'Vocabulary — 3 steps',
    benefitGrammar: '182-day grammar plan',
    benefitVocab: 'New words and exercises daily',
    benefitInteractive: 'Interactive tasks',
    benefitGames: 'Learn through games',
    benefitStats: 'Stats and streaks',
    benefitLeaderboard: 'Leaderboard and motivation',
    whySmartTitle: 'Smart plan',
    whySmartDesc: 'Clear daily tasks',
    whyGameTitle: 'Game format',
    whyGameDesc: 'Fun exercises keep you motivated',
    whyStatsTitle: 'Track progress',
    whyStatsDesc: 'See your results and improve',
    whyLeaderboardTitle: 'Leaderboard',
    whyLeaderboardDesc: 'Compete with others',
    vocabStepLearnDesc: 'Learn words with flashcards',
    vocabStepTestDesc: 'Test your knowledge',
    vocabStepPairsDesc: 'Match Russian and Uzbek pairs',
  },
  rahmat: {
    title: 'Payment received',
    body: 'If access is not open yet, refresh your profile in a few seconds.',
    goProfile: 'Go to profile',
    goHome: 'Home',
  },
  kunlik: {
    noQuestions: 'No questions for this day',
    selectAnswer: 'Choose the correct answer',
    arrangeSentence: 'Arrange the sentence',
    clear: 'Clear',
    learnedWords: 'Words learned:',
    stepLearn: 'Learn',
    stepTest: 'Test',
    stepPairs: 'Match pairs',
    completed: 'Completed',
    daySpeaking: 'Day {{day}} · Speaking',
    seqHint2: 'Complete task 1 first',
    seqHint3: 'Complete task 2 first',
    freeLimitTitle: 'Subscription required',
    freeLimitBody:
      'The daily course unlocks with a full subscription. After payment, all 182 days are available.',
    chooseTariff: 'Choose a plan',
    continueCta: 'Tap to continue',
  },
  common: {
    user: 'User',
    save: 'Save',
    cancel: 'Cancel',
    back: 'Back',
    loading: 'Loading...',
    loadError: 'Failed to load',
    retry: 'Try again',
    today: 'Today',
    close: 'Close',
    copy: 'Copy',
    copied: 'Copied',
    share: 'Share',
    saving: 'Saving...',
    finish: 'Finish',
    start: 'Start',
    send: 'Send',
    unknown: 'Unknown',
    pageNotFound: 'Page not found.',
  },
  language: {
    selectTitle: 'Choose language',
    selectSubtitle: 'Interface language',
  },
  install: {
    cardTitle: 'Add the app to your home screen',
    cardSubtitle: 'Open it in one tap, without the browser',
    action: 'Add',
    dismiss: 'Later',
    profileRow: 'Add to home screen',
    guideTitle: 'Add to home screen',
    guideIosIntro: 'On iPhone you add it through Safari:',
    guideIosStep1: 'Tap the Share button at the bottom (square with an up arrow).',
    guideIosStep2: 'Scroll the list and choose "Add to Home Screen".',
    guideIosStep3: 'Tap "Add" in the top right — the icon appears on your home screen.',
    guideAndroidIntro: 'Through the browser menu:',
    guideAndroidStep1: 'Open the menu (⋮) in the top right.',
    guideAndroidStep2: 'Choose "Install app" or "Add to Home screen".',
    guideAndroidStep3: 'Confirm — the icon appears on your home screen.',
    guideDesktopIntro: 'On desktop, from the address bar:',
    guideDesktopStep1: 'Click the install icon at the right of the address bar.',
    guideDesktopStep2: 'Confirm "Install".',
    installed: 'App installed!',
    dismissedHint: 'You cancelled the install. You can try again any time.',
    close: 'Close',
  },
  auth: {
    benefits: {
    },
    register: 'Sign up',
    logIn: 'Log in',
    logInTitle: 'Log in',
    createAccountTitle: 'Create account',
    phone: 'Phone',
    email: 'Email',
    password: 'Password',
    createPassword: 'Create a password',
    rewritePassword: 'Re-enter password',
    name: 'First name',
    surname: 'Last name',
    emailHint: 'username@gmail.com',
    forgotPassword: 'Forgot password?',
    or: 'OR',
    continueWithGoogle: 'Continue with Google',
    noAccount: "Don't have an account? ",
    signUp: 'Sign up',
    haveAccount: 'Already have an account? ',
    emailRequired: 'Email is required',
    emailInvalid: 'Invalid email',
    phoneInvalid: 'Invalid phone number',
    passwordRequired: 'Password is required',
    passwordMinLength: 'Password must be at least 6 characters',
    passwordsMismatch: 'Passwords do not match',
    nameRequired: 'First name is required',
    surnameRequired: 'Last name is required',
    termsPrefix: 'By creating an account I agree to the ',
    termsOfUse: 'Terms of Use',
    termsAnd: ' and ',
    privacyPolicy: 'Privacy Policy',
    termsSuffix: '.',
    resetTitle: 'Reset password',
    resetSubtitle: 'We will send a new password to your email',
    resetSendPassword: 'Send new password',
    resetSuccessTitle: 'Email sent',
    resetSuccessSubtitle: 'If an account exists, a new password has been sent to your email',
    networkError: 'No internet connection. Please try again.',
    genericError: 'Something went wrong',
  },
  liveStream: {
    badge: 'LIVE',
    title: 'Live stream',
    join: 'Join the stream',
    watchingHint: 'Tap the mic to speak and the camera so others can see you',
    callTitle: 'The live stream has started',
    callJoin: 'Join',
    callDecline: 'Later',
    pushAllow: 'Allow',
    pushModalTitle: 'We’ll ring you when the stream starts',
    pushModalBody: 'Allow notifications and your phone will alert you the moment a live stream begins — even with the app closed.',
    pushLater: 'Later',
    upcomingTitle: 'Upcoming streams',
    noneTitle: 'No live stream right now',
    noneSubtitle: 'You will see a notice on the home page and in the Chat section when one starts',
    back: 'Back',
    loadError: 'Could not load stream data',
  },
};

export default en;
